import { Button, MenuItem, Search, Select, Text } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSearch } from '@/hooks/useSearch';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { UserSyncApi } from '@/utilities/api/UserSyncApi';
import { UserSyncEndpoint } from '@api/endpoints/UserSyncEndpoint';
import {
  Alert,
  Chip,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { UserType } from '@shared/enums';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import AlignAllDialog from './AlignAllDialog';
import PhoneChoiceDialog from './PhoneChoiceDialog';

type Pending = { kind: 'deleteAccount' | 'removeLp', row: Row };
type Row = UserSyncEndpoint.Row;

/** The products a sign-in keeps after the action: it is deleted only when none are left. */
const productsLeft = ({ kind, row }: Pending): string[] => {
  const platforms = row.person?.platforms ?? [];
  const keepsLp = kind === 'deleteAccount' && (row.person?.accountIds.length ?? 0) > 1;
  return keepsLp ? platforms : platforms.filter((product) => product !== 'LP');
};
type Status = UserSyncEndpoint.Status;
type StatusFilter = UserSyncEndpoint.StatusFilter;

const AlignPollMs = 5000;
const NoWrap = { whiteSpace: 'nowrap' };
const PageSize = 25;
const StatusColors: Record<Status, 'error' | 'success' | 'warning'> = {
  'cognito-only': 'warning',
  conflict: 'error',
  'db-only': 'warning',
  linked: 'success',
};
// The chips' display order, from aligned to needing attention.
const StatusFilters: StatusFilter[] = ['linked', 'db-only', 'cognito-only', 'conflicts'];
const UserTypes = [UserType.Internal, UserType.RetailerAdmin, UserType.SalesPerson];

// The span lets a disabled button still show its tooltip.
const withTooltip = (title: string, button: React.ReactElement) => (
  <Tooltip title={title}><span>{button}</span></Tooltip>
);

const rowKey = (row: Row) => `${row.account?.id ?? ''}:${row.person?.username ?? ''}`;

/** Sign-in sync: LP's accounts against the shared Cognito pool, for internal users. */
const UserSync = () => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<StatusFilter | undefined>();
  const [userType, setUserType] = useState<UserType | ''>('');
  const { search, setSearch, debouncedSearch } = useSearch();
  const [pending, setPending] = useState<Pending | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [showAlignDialog, setShowAlignDialog] = useState(false);
  const [phoneRow, setPhoneRow] = useState<Row | null>(null);

  useEffect(() => setPage(0), [debouncedSearch]);

  const { data: alignProgress, dataUpdatedAt: alignCheckedAt } = useQuery(
    QueryKeys.GET_USER_SYNC_ALIGN_PROGRESS,
    () => UserSyncApi.getAlignProgress(),
    { refetchInterval: (progress) => (progress?.isRunning ? AlignPollMs : false) },
  );
  const isAligning = !!alignProgress?.isRunning;
  const wasAligning = useRef(false);
  const lastAlignCheckAt = useRef(0);

  // Acts on every new check, so a run that ends before the first one still refreshes the rows.
  useEffect(() => {
    if (alignCheckedAt === lastAlignCheckAt.current) {
      return;
    }
    lastAlignCheckAt.current = alignCheckedAt;
    if (wasAligning.current && !isAligning) {
      openSnackbar(t(($) => $.userSync.align.finished));
      void queryClient.invalidateQueries(QueryKeys.LIST_USER_SYNC);
    }
    wasAligning.current = isAligning;
  }, [alignCheckedAt, isAligning, openSnackbar, queryClient, t]);

  const onAlignStarted = () => {
    wasAligning.current = true;
    void queryClient.invalidateQueries(QueryKeys.GET_USER_SYNC_ALIGN_PROGRESS);
  };

  const query: UserSyncEndpoint.List.Query = {
    limit: PageSize,
    page,
    search: debouncedSearch.trim() || undefined,
    status,
    userType: userType || undefined,
  };
  const { data, isError, isLoading } = useQuery(
    [QueryKeys.LIST_USER_SYNC, query],
    () => UserSyncApi.list(query),
    { keepPreviousData: true },
  );

  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const confirm = () => {
    if (!pending) {
      return;
    }
    const { kind, row: { account, person } } = pending;
    setPending(null);
    if (kind === 'removeLp' && person) {
      void run(() => UserSyncApi.removeLp({ username: person.username }));
    } else if (kind === 'deleteAccount' && account) {
      void run(() => UserSyncApi.deleteAccount({ userId: account.id }));
    }
  };

  const signInNote = (action: Pending) => {
    const isRemoveLp = action.kind === 'removeLp';
    const ownsSignIn = isRemoveLp || action.row.status === 'linked';
    if (!action.row.person || !ownsSignIn) {
      return t(($) => $.userSync.confirm.deleteAccountNoSignIn);
    }
    const products = productsLeft(action).join(', ');
    if (!products) {
      return isRemoveLp
        ? t(($) => $.userSync.confirm.removeLpDeletesSignIn)
        : t(($) => $.userSync.confirm.deleteAccountDeletesSignIn);
    }
    return isRemoveLp
      ? t(($) => $.userSync.confirm.removeLpKeeps, { products })
      : t(($) => $.userSync.confirm.deleteAccountKeeps, { products });
  };

  const fixFor = (row: Row) => {
    const { account, person } = row;
    const canSignIn = row.status === 'db-only' && !row.signInRefused;
    if (canSignIn && account && person) {
      return withTooltip(t(($) => $.userSync.tooltips.link), (
        <Button
          disabled={isBusy}
          onClick={() => void run(() => UserSyncApi.link({
            userId: account.id,
            username: person.username,
          }))}
          size="small"
          testID="user-sync-link"
          variant="outlined"
        >
          {t(($) => $.userSync.actions.link)}
        </Button>
      ));
    }
    if (canSignIn && account) {
      return withTooltip(t(($) => $.userSync.tooltips.createSignIn), (
        <Button
          disabled={isBusy}
          onClick={() => void run(() => UserSyncApi.createSignIn({ userId: account.id }))}
          size="small"
          testID="user-sync-create-sign-in"
          variant="outlined"
        >
          {t(($) => $.userSync.actions.createSignIn)}
        </Button>
      ));
    }
    if (row.status === 'linked' && row.phones) {
      return withTooltip(t(($) => $.userSync.tooltips.choosePhone), (
        <Button
          color="warning"
          disabled={isBusy}
          onClick={() => setPhoneRow(row)}
          size="small"
          testID="user-sync-choose-phone"
          variant="outlined"
        >
          {t(($) => $.userSync.actions.choosePhone)}
        </Button>
      ));
    }
    if (row.status === 'cognito-only') {
      return withTooltip(signInNote({ kind: 'removeLp', row }), (
        <Button
          color="error"
          disabled={isBusy}
          onClick={() => setPending({ kind: 'removeLp', row })}
          size="small"
          testID="user-sync-remove-lp"
          variant="outlined"
        >
          {t(($) => $.userSync.actions.removeLp)}
        </Button>
      ));
    }
    return null;
  };

  const choosePhone = (keep: UserSyncEndpoint.KeepPhone.Request['keep']) => {
    const { account, person } = phoneRow ?? {};
    setPhoneRow(null);
    if (account && person) {
      const request = { keep, userId: account.id, username: person.username };
      void run(() => UserSyncApi.keepPhone(request));
    }
  };

  const run = async (action: () => Promise<{ message: string }>) => {
    setIsBusy(true);
    try {
      const { message } = await action();
      openSnackbar(message);
      await queryClient.invalidateQueries(QueryKeys.LIST_USER_SYNC);
    } catch (error) {
      openSnackbar((error as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Stack gap={3}>
      <Stack alignItems="flex-start" direction="row" gap={2} justifyContent="space-between">
        <Stack gap={0.5}>
          <Text category="title-large">{t(($) => $.userSync.title)}</Text>
          <Text sx={{ color: 'text.secondary' }}>{t(($) => $.userSync.description)}</Text>
        </Stack>
        <Button
          loading={isAligning}
          onClick={() => setShowAlignDialog(true)}
          testID="user-sync-align-all"
          variant="outlined"
        >
          {t(($) => $.userSync.actions.alignAll)}
        </Button>
      </Stack>

      <Stack gap={2}>
        <Stack
          aria-label={t(($) => $.userSync.statusFilter)}
          direction="row"
          flexWrap="wrap"
          gap={1}
          role="group"
        >
          <Chip
            color={status === undefined ? 'primary' : 'default'}
            label={t(($) => $.userSync.all)}
            onClick={() => changeFilter(() => setStatus(undefined))}
          />
          {StatusFilters.map((value) => (
            <Chip
              color={status === value ? 'primary' : 'default'}
              key={value}
              label={`${t(($) => $.userSync.status[value])} (${data?.counts[value] ?? 0})`}
              onClick={() => changeFilter(() => setStatus(value))}
            />
          ))}
        </Stack>

        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={2}>
          <Search
            onChangeText={setSearch}
            placeholder={t(($) => $.userSync.search)}
            testID="user-sync-search"
            value={search}
            width={560}
          />
          <Select
            label={t(($) => $.userSync.userType)}
            onChangeValue={(value) => changeFilter(() => setUserType(value as UserType | ''))}
            testID="user-sync-user-type"
            value={userType}
            width={220}
          >
            <MenuItem testID="user-sync-user-type-all" value="">
              {t(($) => $.userSync.all)}
            </MenuItem>
            {UserTypes.map((type) => (
              <MenuItem key={type} testID={`user-sync-user-type-${type}`} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      {isError ? (
        <Alert severity="error">{t(($) => $.userSync.loadError)}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label={t(($) => $.userSync.title)} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...NoWrap, width: 160 }}>
                  {t(($) => $.userSync.columns.status)}
                </TableCell>
                <TableCell sx={{ ...NoWrap, minWidth: 260 }}>
                  {t(($) => $.userSync.columns.account)}
                </TableCell>
                <TableCell sx={{ ...NoWrap, minWidth: 280 }}>
                  {t(($) => $.userSync.columns.signIn)}
                </TableCell>
                <TableCell sx={NoWrap}>{t(($) => $.userSync.columns.products)}</TableCell>
                <TableCell align="right" sx={NoWrap}>
                  {t(($) => $.userSync.columns.actions)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5}>{t(($) => $.userSync.loading)}</TableCell>
                </TableRow>
              )}
              {!isLoading && !data?.data.length && (
                <TableRow><TableCell colSpan={5}>{t(($) => $.userSync.empty)}</TableCell></TableRow>
              )}
              {data?.data.map((row) => (
                <TableRow data-testid="user-sync-row" key={rowKey(row)}>
                  <TableCell>
                    <Stack alignItems="flex-start" gap={0.5}>
                      <Chip
                        color={StatusColors[row.status]}
                        label={t(($) => $.userSync.status[row.status])}
                        size="small"
                      />
                      {row.reason && (
                        <Text category="body-small" color="error">{row.reason}</Text>
                      )}
                      {row.signInRefused && (
                        <Text category="body-small" color="error">
                          {t(($) => $.userSync.signInRefused)}
                        </Text>
                      )}
                      {row.phones && (
                        <Text category="body-small" color="warning">
                          {t(($) => $.userSync.phonesDiffer)}
                        </Text>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.account ? (
                      <>
                        <Text breakWord>{row.account.name}</Text>
                        {row.account.retailerName && (
                          <Text breakWord category="body-small">{row.account.retailerName}</Text>
                        )}
                        <Text breakWord category="body-small" sx={{ color: 'text.secondary' }}>
                          {[row.account.email, row.account.telephone, row.account.userType]
                            .filter(Boolean).join(' · ')}
                        </Text>
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {row.person ? row.person.email ?? row.person.phone : '—'}
                  </TableCell>
                  <TableCell sx={NoWrap}>{row.person?.platforms.join(', ') || '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      {fixFor(row)}
                      {row.account && withTooltip(signInNote({ kind: 'deleteAccount', row }), (
                        <Button
                          color="error"
                          disabled={isBusy}
                          onClick={() => setPending({ kind: 'deleteAccount', row })}
                          size="small"
                          testID="user-sync-delete-account"
                          variant="outlined"
                        >
                          {t(($) => $.userSync.actions.deleteAccount)}
                        </Button>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data && data.lastPage > 0 && (
        <Stack alignItems="center">
          <Pagination
            count={data.lastPage + 1}
            onChange={(_, value) => setPage(value - 1)}
            page={data.page + 1}
          />
        </Stack>
      )}

      <ConfirmDialog
        confirmText={t(($) => (pending?.kind === 'removeLp'
          ? $.userSync.actions.removeLp
          : $.userSync.actions.deleteAccount))}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
        open={pending !== null}
        testID="user-sync-confirm"
        title={t(($) => (pending?.kind === 'removeLp'
          ? $.userSync.confirm.removeLpTitle
          : $.userSync.confirm.deleteAccountTitle))}
      >
        <Text>{pending && signInNote(pending)}</Text>
      </ConfirmDialog>

      <PhoneChoiceDialog onChoose={choosePhone} onClose={() => setPhoneRow(null)} row={phoneRow} />

      <AlignAllDialog
        onClose={() => {
          setShowAlignDialog(false);
          void queryClient.invalidateQueries(QueryKeys.LIST_USER_SYNC);
        }}
        onStarted={onAlignStarted}
        open={showAlignDialog}
      />
    </Stack>
  );
};

export default UserSync;
